// SM25 packet building, parsing, checksum, and guide-frame handling
// Direct port of Sm25Protocol.java

import { Sm25Serial } from './sm25Serial';
import {
	PREFIX_RESP,
	PREFIX_RESPDAT,
	PREFIX_CMD,
	PREFIX_DATA,
	ERR_SUCCESS,
	ERR_BAD_QUALITY,
	isGuideCode,
	codeName,
} from './types';

function isResponseData(p) {
	return 'data' in p && p.data.length !== 14;
}

export class Sm25Protocol {
	constructor(serial) {
		this.serial = serial;
		this.guideCallback = null;
	}

	setGuideCallback(cb) {
		this.guideCallback = cb;
	}

	// --- Packet building ---

	static buildCommandPacket(cmd, params) {
		const p = params || new Uint8Array(0);
		if (p.length > 16) throw new Error(`params too long: ${p.length}`);

		const buf = new DataView(new ArrayBuffer(24));
		buf.setUint16(0, PREFIX_CMD, true);
		buf.setUint16(2, cmd, true);
		buf.setUint16(4, p.length, true);

		const arr = new Uint8Array(buf.buffer);
		arr.set(p, 6);

		const cks = checksum(arr, 0, 22);
		buf.setUint16(22, cks, true);
		return arr;
	}

	static buildDataPacket(cmd, data) {
		const totalLen = 6 + data.length + 2;
		const buf = new DataView(new ArrayBuffer(totalLen));
		buf.setUint16(0, PREFIX_DATA, true);
		buf.setUint16(2, cmd, true);
		buf.setUint16(4, data.length, true);

		const arr = new Uint8Array(buf.buffer);
		arr.set(data, 6);

		const cks = checksum(arr, 0, 6 + data.length);
		buf.setUint16(6 + data.length, cks, true);
		return arr;
	}

	// --- Packet reading ---

	async readPacket(timeoutMs) {
		const hdr = await this.serial.readExact(6, timeoutMs);
		const view = new DataView(hdr.buffer, hdr.byteOffset, hdr.byteLength);

		const prefix = view.getUint16(0, true);
		const code = view.getUint16(2, true);
		const len = view.getUint16(4, true);

		if (prefix === PREFIX_RESP) {
			const rest = await this.serial.readExact(18, timeoutMs);
			const full = concat(hdr, rest);
			const fv = new DataView(full.buffer, full.byteOffset, full.byteLength);

			const rcm = fv.getUint16(2, true);
			const ret = fv.getUint16(6, true);
			const data = full.slice(8, 22);
			const cks = fv.getUint16(22, true);
			const calc = checksum(full, 0, 22);

			if (cks !== calc) throw new Error('Checksum mismatch (response)');

			return { rcm, len, ret, data };
		}

		if (prefix === PREFIX_RESPDAT) {
			const rest = await this.serial.readExact(len + 2, timeoutMs);
			const full = concat(hdr, rest);
			const fv = new DataView(full.buffer, full.byteOffset, full.byteLength);

			const rcm = fv.getUint16(2, true);
			const ret = fv.getUint16(6, true);
			const dataLen = (len & 0xffff) - 2;
			const data = dataLen > 0 ? full.slice(8, 8 + dataLen) : new Uint8Array(0);
			const cks = fv.getUint16(6 + (len & 0xffff), true);
			const calc = checksum(full, 0, 6 + (len & 0xffff));

			if (cks !== calc) throw new Error('Checksum mismatch (response data)');

			return { rcm, len, ret, data };
		}

		throw new Error(`Unexpected prefix: 0x${prefix.toString(16)}`);
	}

	// --- Guide frame handling ---

	_emitGuide(code) {
		if (!this.guideCallback) return;
		const name = codeName(code);
		this.guideCallback(code, name);
	}

	async recvResponseSkippingGuides(timeoutMs) {
		for (;;) {
			const p = await this.readPacket(timeoutMs);
			if (isResponseData(p)) throw new Error('Unexpected ResponseData packet');

			const r = p;
			const dataCode = u16LE(r.data, 0);

			if (r.ret !== ERR_SUCCESS) {
				if (dataCode === ERR_BAD_QUALITY) continue;
				return r;
			}

			if (isGuideCode(dataCode)) {
				this._emitGuide(dataCode);
				continue;
			}

			return r;
		}
	}

	async _recvPacketSkippingGuidesUntilDataOrFinal(timeoutMs) {
		for (;;) {
			const p = await this.readPacket(timeoutMs);

			if (isResponseData(p)) return p;

			const r = p;
			if (r.ret !== ERR_SUCCESS) return r;

			const dataCode = u16LE(r.data, 0);
			if (isGuideCode(dataCode)) {
				this._emitGuide(dataCode);
				continue;
			}

			return r;
		}
	}

	// --- High-level send methods ---

	async sendCommand(cmd, params, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 10000;
		const pkt = Sm25Protocol.buildCommandPacket(cmd, params);
		await this.serial.write(pkt);
		return this.recvResponseSkippingGuides(timeoutMs);
	}

	async sendCommandExpectData(cmd, params, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 10000;
		const pkt = Sm25Protocol.buildCommandPacket(cmd, params);
		await this.serial.write(pkt);

		const resp = await this.recvResponseSkippingGuides(timeoutMs);
		if (resp.ret !== ERR_SUCCESS) return { header: resp, data: null };

		const dp = await this.readPacket(timeoutMs);
		if (!isResponseData(dp)) throw new Error('Expected ResponseData packet');

		return { header: resp, data: dp };
	}

	async sendCommandThenUploadData(cmd, params, payload, timeoutMs) {
		if (timeoutMs === undefined) timeoutMs = 10000;
		// 1) Send command
		const pkt = Sm25Protocol.buildCommandPacket(cmd, params);
		await this.serial.write(pkt);

		// 2) Read "enter data receive state" response
		const resp = await this.recvResponseSkippingGuides(timeoutMs);
		if (resp.ret !== ERR_SUCCESS) return resp;

		// 3) Send data packet
		const dp = Sm25Protocol.buildDataPacket(cmd, payload);
		await this.serial.write(dp);

		// 4) Read final result
		const rp = await this._recvPacketSkippingGuidesUntilDataOrFinal(timeoutMs);
		if (isResponseData(rp)) {
			return {
				rcm: rp.rcm,
				len: 2,
				ret: rp.ret,
				data: rp.data.length >= 2 ? rp.data.slice(0, 2) : new Uint8Array(2),
			};
		}

		return rp;
	}
}

// --- Utility functions ---

function checksum(arr, start, len) {
	let sum = 0;
	for (let i = start; i < start + len; i++) {
		sum += arr[i];
	}
	return sum & 0xffff;
}

function concat(a, b) {
	const out = new Uint8Array(a.length + b.length);
	out.set(a, 0);
	out.set(b, a.length);
	return out;
}

export function u16LE(data, offset) {
	if (!data || data.length < offset + 2) return 0;
	return (data[offset] & 0xff) | ((data[offset + 1] & 0xff) << 8);
}

export function le16(v) {
	const buf = new Uint8Array(2);
	buf[0] = v & 0xff;
	buf[1] = (v >> 8) & 0xff;
	return buf;
}
